import { MonitorSmartphone, ShoppingBag, LayoutDashboard, Cpu, Check } from "lucide-react";
import { PageHeader, Reveal, Eyebrow } from "@/components/site/Reveal";
import Tech from "@/components/site/Tech";
import Process from "@/components/site/Process";
import FinalCTA from "@/components/site/FinalCTA";
import { SERVICES } from "@/data/site";

const ICONS = { MonitorSmartphone, ShoppingBag, LayoutDashboard, Cpu };

const ServiceBlock = ({ service, index }) => {
    const Icon = ICONS[service.icon];
    return (
        <Reveal delay={0.05}>
            <article
                data-testid={`service-detail-${service.num}`}
                className="grid grid-cols-1 items-start gap-10 border-t border-brand/15 py-14 first:border-t-0 first:pt-0 lg:grid-cols-2 lg:gap-16"
            >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                    <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-card">
                            <Icon className="h-6 w-6" strokeWidth={1.8} />
                        </div>
                        <span className="font-mono text-sm font-semibold tracking-[0.25em] text-brand/50">
                            {service.num}
                        </span>
                    </div>
                    <h2 className="mt-7 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                        {service.title}
                    </h2>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                        {service.long}
                    </p>
                    <div className="mt-7 flex flex-wrap gap-2">
                        {service.tags.map((t) => (
                            <span
                                key={t}
                                className="rounded-full border border-brand/20 bg-brand-subtle px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-brand-dark"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                    <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-card sm:p-8">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-brand">
                            What's included
                        </p>
                        <ul className="mt-5 space-y-3.5">
                            {service.deliverables.map((d) => (
                                <li key={d} className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-subtle">
                                        <Check className="h-3 w-3 text-brand" strokeWidth={3} aria-hidden="true" />
                                    </span>
                                    {d}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </article>
        </Reveal>
    );
};

export default function ServicesPage() {
    return (
        <div data-testid="services-page">
            <PageHeader
                eyebrow="Services"
                title={
                    <>
                        Engineering services, <span className="text-brand">end to end</span>.
                    </>
                }
                sub="Four focused practice areas covering everything from marketing sites to custom software — each scoped around your business goals, not a template."
            />
            <section className="bg-gradient-to-b from-paper to-blue-50/50 py-20 sm:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <Reveal>
                        <Eyebrow testId="services-detail-eyebrow">In detail</Eyebrow>
                    </Reveal>
                    {SERVICES.map((s, i) => (
                        <ServiceBlock key={s.num} service={s} index={i} />
                    ))}
                </div>
            </section>
            <Tech />
            <Process />
            <FinalCTA />
        </div>
    );
}
